/**
 * Format pricing quantities with a unit label.
 * Dashboard pricing uses Rookoo short labels; quotes use Peppol UNECE Rec20 names.
 * https://docs.peppol.eu/poacc/billing/3.0/codelist/UNECERec20/
 */

const UBL_CODE_BY_UNIT = {
  per_hour: 'HUR',
  per_day: 'DAY',
  per_guest: 'IE',
  per_event: 'C62',
  per_quantity: 'C62',
  per_hour_per_guest: 'C62',
};

const UBL_I18N_KEY = {
  HUR: 'PLANNING.SHOW.PEPPOL_UNITS.HOUR',
  DAY: 'PLANNING.SHOW.PEPPOL_UNITS.DAY',
  IE: 'PLANNING.SHOW.PEPPOL_UNITS.PERSON',
  C62: 'PLANNING.SHOW.PEPPOL_UNITS.ONE',
};

const PRICING_SHORT_KEY = {
  per_hour: 'PRICING.UNITS_SHORT.PER_HOUR',
  per_day: 'PRICING.UNITS_SHORT.PER_DAY',
  per_guest: 'PRICING.UNITS_SHORT.PER_GUEST',
  per_hour_per_guest: 'PRICING.UNITS_SHORT.PER_HOUR_PER_GUEST',
  per_quantity: 'PRICING.UNITS_SHORT.PER_QUANTITY',
  per_event: 'PRICING.UNITS_SHORT.PER_EVENT',
};

const PRICING_PHRASE_KEY = {
  per_hour: 'PRICING.UNITS_PHRASE.PER_HOUR',
  per_day: 'PRICING.UNITS_PHRASE.PER_DAY',
  per_guest: 'PRICING.UNITS_PHRASE.PER_GUEST',
  per_hour_per_guest: 'PRICING.UNITS_PHRASE.PER_HOUR_PER_GUEST',
  per_quantity: 'PRICING.UNITS_PHRASE.PER_QUANTITY',
  per_event: 'PRICING.UNITS_PHRASE.PER_EVENT',
};

export function ublUnitCode(unit) {
  return UBL_CODE_BY_UNIT[unit] || 'C62';
}

export function peppolUnitLabel(unit, t) {
  return t(UBL_I18N_KEY[ublUnitCode(unit)]);
}

export function pricingUnitShortLabel(unit, t) {
  const key = PRICING_SHORT_KEY[unit];
  return key ? t(key) : '';
}

export function formatQtyWithUnit(qty, unit, t, { peppol = false } = {}) {
  if (qty == null || qty === '') return null;
  const label = peppol
    ? peppolUnitLabel(unit, t)
    : pricingUnitShortLabel(unit, t);
  if (!label) return String(qty);
  return `${qty} / ${label}`;
}

/** Natural phrase for schedule: "2 hours", "1 event", "50 guests". */
export function formatQtyUnitPhrase(qty, unit, t) {
  if (qty == null || qty === '') return null;
  const key = PRICING_PHRASE_KEY[unit];
  if (!key) return String(qty);
  const count = Number(qty);
  const display = Number.isInteger(count) ? count : qty;
  return t(key, { count: display });
}

/**
 * Schedule breakdown formula by unit type:
 * - per_hour / per_day / per_guest / … → `crew 10 hours * €50`
 * - per_quantity → `20 cake * €3`
 * - per_event → `Cleaning * €100` (or `Cleaning 2 events * €100`)
 */
export function formatSlotPricingFormula(line, formatRate, t) {
  const name = (line?.name || '').trim() || '—';
  const rate =
    line?.unit_price != null && formatRate
      ? formatRate(line.unit_price)
      : null;
  const ratePart = rate ? ` x ${rate}` : '';
  const qty = line?.quantity;
  const unit = line?.unit;
  const phrase = formatQtyUnitPhrase(qty, unit, t);

  if (unit === 'per_quantity') {
    const qtyLabel = qty == null || qty === '' ? null : String(qty);
    if (qtyLabel) return `${qtyLabel} ${name}${ratePart}`;
    return `${name}${ratePart}`;
  }

  if (unit === 'per_event') {
    const count = Number(qty);
    if (!qty && qty !== 0) return `${name}${ratePart}`;
    if (count === 1) return `${name}${ratePart}`;
    if (phrase) return `${name} ${phrase}${ratePart}`;
    return `${name}${ratePart}`;
  }

  if (phrase) return `${name} ${phrase}${ratePart}`;
  return `${name}${ratePart}`;
}
